import { memo } from 'react';
import { clsx } from 'clsx';

type StatusVariant = 'available' | 'tentative' | 'booked' | 'cancelled' |
  'pending' | 'validating' | 'approved' | 'rejected' | 'expired' |
  'open' | 'filled' | 'closed' | 'applied' | 'shortlisted' | 'accepted' |
  'contacted' | 'onboarding' | 'activated' | 'rewarded' |
  'urgent' | 'high' | 'normal' | 'low';

const STATUS_CONFIG: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  available: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Disponible' },
  tentative: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', label: 'Tentativo' },
  booked: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Reservado' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Cancelado' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', label: 'Pendiente' },
  validating: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Validando' },
  approved: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Aprobado' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: 'Rechazado' },
  expired: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Expirado' },
  open: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Abierta' },
  filled: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Cubierta' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Cerrada' },
  applied: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Aplicada' },
  shortlisted: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500', label: 'Preseleccionado' },
  accepted: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Aceptado' },
  contacted: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Contactado' },
  onboarding: { bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500', label: 'Onboarding' },
  activated: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500', label: 'Activado' },
  rewarded: { bg: 'bg-brand-yellow/20', text: 'text-brand-black', dot: 'bg-brand-yellow', label: 'Recompensado' },
  urgent: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: 'Urgente' },
  high: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500', label: 'Alta' },
  normal: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: 'Normal' },
  low: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: 'Baja' },
};

interface StatusBadgeProps {
  status: StatusVariant | string;
  showDot?: boolean;
  className?: string;
}

function StatusBadgeInner({ status, showDot = true, className }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] || { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', label: status };

  return (
    <span className={clsx('inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full', config.bg, config.text, className)}>
      {showDot && <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />}
      {config.label}
    </span>
  );
}

export const StatusBadge = memo(StatusBadgeInner);
StatusBadge.displayName = 'StatusBadge';
